import { useState, useEffect } from 'react';

export function useForm<InitialState>(
  initialState: InitialState,
): [
  InitialState,
  <Value>(field?: string | InitialState, value?: Value) => void,
] {
  const [state, setState] = useState(initialState);

  return [
    state,
    function (field, value): void {
      if (field === 'reset') {
        return setState(initialState);
      }

      if (typeof field !== 'string') {
        return setState((prevState) => ({
          ...prevState,
          ...field,
        }));
      }

      return setState((prevState) => ({
        ...prevState,
        [field]: value,
      }));
    },
  ];
}

export function useDebounce<Value>(value: Value, delay: number): Value {
  // State and setters for debounced value
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(
    () => {
      // Set debouncedValue to value (passed in) after the specified delay
      const handler = setTimeout(() => {
        setDebouncedValue(value);
      }, delay);

      // Return a cleanup function that will be called every time ...
      // ... useEffect is re-called. useEffect will only be re-called ...
      // ... if value changes (see the inputs array below).
      // This is how we prevent debouncedValue from changing if value is ...
      // ... changed within the delay period. Timeout gets cleared and restarted.
      // To put it in context, if the user is typing within our app's ...
      // ... search box, we don't want the debouncedValue to update until ...
      // ... they've stopped typing for more than 500ms.
      return (): void => {
        clearTimeout(handler);
      };
    },
    // Only re-call effect if value changes
    // You could also add the "delay" var to inputs array if you ...
    // ... need to be able to change that dynamically.
    [value, delay],
  );

  return debouncedValue;
}
